import { useState } from "react";
import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { Multiplier, CategoryData } from "@/api/multipliers";
import { MultiplierCard } from "./MultiplierCard";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";

interface KPIGroupProps {
    group: CategoryData | Multiplier;
    editedMultipliers: Map<number, Partial<Multiplier>>;
    onInputChange: (
        id: number,
        field: keyof Pick<Multiplier, "weight" | "maxPoints" | "isActive">,
        value: string | boolean
    ) => void;
    onResetToDefault: (multiplier: Multiplier) => void;
    isLoading: boolean;
}

export function KPIGroup({ group, editedMultipliers, onInputChange, onResetToDefault, isLoading }: KPIGroupProps) {
    const [isExpanded, setIsExpanded] = useState(true);

    const isMultiplier = (obj: CategoryData | Multiplier): obj is Multiplier => {
        return "id" in obj && "statName" in obj && !("max" in obj) && !("weights" in obj);
    };

    if (isMultiplier(group)) {
        const totalKPI = group as Multiplier;

        const getCurrentValue = <T extends keyof Pick<Multiplier, "weight" | "maxPoints" | "isActive">>(
            multiplier: Multiplier,
            field: T
        ): Multiplier[T] => {
            const edited = editedMultipliers.get(multiplier.id);
            return edited?.[field] !== undefined ? (edited[field] as Multiplier[T]) : multiplier[field];
        };

        const hasChanges = (multiplier: Multiplier): boolean => {
            const edited = editedMultipliers.get(multiplier.id);
            if (!edited) return false;

            return (
                (edited.weight !== undefined && edited.weight !== multiplier.weight) ||
                (edited.maxPoints !== undefined && edited.maxPoints !== multiplier.maxPoints) ||
                (edited.isActive !== undefined && edited.isActive !== multiplier.isActive)
            );
        };

        const formatStatName = (name: string): string => {
            return name
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        };

        const isActive = getCurrentValue(totalKPI, "isActive");
        const isChanged = hasChanges(totalKPI);
        const isWeightDisabled = (totalKPI.weight === null && totalKPI.defaultWeight === null) || isLoading;
        const isMaxPointsDisabled = (totalKPI.maxPoints === null && totalKPI.defaultMaxPoints === null) || isLoading;

        return (
            <div
                className={`relative rounded-xl shadow-sm overflow-hidden transition-all duration-300 ${
                    !isActive
                        ? "opacity-40 bg-surface-container-lowest border border-outline-variant/40"
                        : isChanged
                          ? "shadow-lg shadow-general-blue/20 bg-general-blue-background border border-general-blue/70"
                          : "bg-surface-container-lowest border border-outline-variant/40"
                }`}
            >
                {isChanged && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-general-blue rounded-full animate-pulse shadow-lg shadow-general-blue/50"></div>
                )}

                {!isActive && (
                    <div className="absolute inset-0 bg-surface-container/20 rounded-lg pointer-events-none"></div>
                )}

                <div className="p-3 sm:p-4">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                <h3
                                    className={`text-base font-semibold ${!isActive ? "text-on-surface-variant" : "text-on-surface"}`}
                                >
                                    {formatStatName(totalKPI.statName)}
                                </h3>
                            </div>

                            <p
                                className={`text-sm mb-4 leading-relaxed ${!isActive ? "text-on-surface-variant" : "text-on-surface-variant"}`}
                            >
                                {totalKPI.description}
                            </p>

                            <div className="flex items-end gap-2 sm:gap-3">
                                <div className="flex flex-col items-center">
                                    <span
                                        className={`text-xs ${!isActive ? "text-on-surface-variant" : "text-on-surface-variant"} block mb-[8px]`}
                                    >
                                        Active
                                    </span>
                                    <label className="relative inline-flex items-center cursor-pointer mb-1">
                                        <input
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={(e) => onInputChange(totalKPI.id, "isActive", e.target.checked)}
                                            disabled={isLoading}
                                            className="sr-only peer"
                                            aria-label={`Toggle ${formatStatName(totalKPI.statName)} active state`}
                                        />
                                        <div
                                            className={`w-9 h-5 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-general-blue/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-disabled:opacity-50 ${
                                                isActive ? "bg-general-blue" : "bg-surface-container"
                                            }`}
                                        ></div>
                                    </label>
                                </div>

                                <div className="flex-1">
                                    <label className="flex items-center justify-between text-xs text-on-surface-variant mb-1">
                                        <span>Weight</span>
                                        {totalKPI.defaultWeight !== null && (
                                            <span className="text-xs text-on-surface-variant">
                                                Def: {totalKPI.defaultWeight}
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={getCurrentValue(totalKPI, "weight") ?? ""}
                                        onChange={(e) => onInputChange(totalKPI.id, "weight", e.target.value)}
                                        placeholder={totalKPI.weight === null ? "N/A" : "Weight"}
                                        disabled={isWeightDisabled}
                                        className="w-full px-2 py-1 text-xs bg-surface-container border border-outline-variant/20 rounded text-on-surface placeholder-on-surface-variant focus:outline-none focus:border-general-blue/50 focus:ring-1 focus:ring-general-blue/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed number-input-no-arrows"
                                        aria-label="Weight value for total KPI"
                                    />
                                </div>

                                <div className="flex-1">
                                    <label className="flex items-center justify-between text-xs text-on-surface-variant mb-1">
                                        <span>Max Points</span>
                                        {totalKPI.defaultMaxPoints !== null && (
                                            <span className="text-xs text-on-surface-variant">
                                                Def: {totalKPI.defaultMaxPoints}
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="number"
                                        step="1"
                                        value={getCurrentValue(totalKPI, "maxPoints") ?? ""}
                                        onChange={(e) => onInputChange(totalKPI.id, "maxPoints", e.target.value)}
                                        placeholder={totalKPI.maxPoints === null ? "N/A" : "Max pts"}
                                        disabled={isMaxPointsDisabled}
                                        className="w-full px-2 py-1 text-xs bg-surface-container border border-outline-variant/20 rounded text-on-surface placeholder-on-surface-variant focus:outline-none focus:border-general-blue/50 focus:ring-1 focus:ring-general-blue/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed number-input-no-arrows"
                                        aria-label="Maximum points value for total KPI"
                                    />
                                </div>

                                <div className="flex items-end">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={() => onResetToDefault(totalKPI)}
                                                className="cursor-pointer p-1 text-on-surface hover:text-on-surface-variant hover:bg-surface-container rounded transition-colors"
                                                disabled={isLoading}
                                            >
                                                <RotateCcw className="w-3 h-3" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Reset to default values</TooltipContent>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const categoryGroup = group as CategoryData;

    if (!categoryGroup.max) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {categoryGroup.weights?.map((multiplier: Multiplier) => (
                    <MultiplierCard
                        key={multiplier.id}
                        multiplier={multiplier}
                        editedMultipliers={editedMultipliers}
                        onInputChange={onInputChange}
                        onResetToDefault={onResetToDefault}
                        isLoading={isLoading}
                    />
                ))}
            </div>
        );
    }

    const hasSubKPIs = categoryGroup.weights && categoryGroup.weights.length > 0;
    const parentKPI = categoryGroup.max;

    const formatStatName = (name: string): string => {
        return name
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const getCurrentValue = <T extends keyof Pick<Multiplier, "weight" | "maxPoints" | "isActive">>(
        multiplier: Multiplier,
        field: T
    ): Multiplier[T] => {
        const edited = editedMultipliers.get(multiplier.id);
        return edited?.[field] !== undefined ? (edited[field] as Multiplier[T]) : multiplier[field];
    };

    const hasChanges = (multiplier: Multiplier): boolean => {
        const edited = editedMultipliers.get(multiplier.id);
        if (!edited) return false;

        return (
            (edited.weight !== undefined && edited.weight !== multiplier.weight) ||
            (edited.maxPoints !== undefined && edited.maxPoints !== multiplier.maxPoints) ||
            (edited.isActive !== undefined && edited.isActive !== multiplier.isActive)
        );
    };

    const parentIsActive = getCurrentValue(parentKPI, "isActive");
    const isChanged = hasChanges(parentKPI);
    const isWeightDisabled = (parentKPI.weight === null && parentKPI.defaultWeight === null) || isLoading;
    const isMaxPointsDisabled = (parentKPI.maxPoints === null && parentKPI.defaultMaxPoints === null) || isLoading;

    const handleCardClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;

        if (!hasSubKPIs) return;

        if (target.closest(".interactive-zone")) {
            return;
        }

        setIsExpanded(!isExpanded);
    };

    return (
        <div
            className={`relative rounded-xl shadow-sm overflow-hidden transition-all duration-300 ${
                !parentIsActive
                    ? "opacity-40 bg-surface-container-lowest border border-outline-variant/40"
                    : isChanged
                      ? "shadow-lg shadow-general-blue/20 bg-general-blue-background border border-general-blue/70"
                      : "bg-surface-container-lowest border border-outline-variant/40"
            }`}
            onClick={handleCardClick}
            role={hasSubKPIs ? "button" : undefined}
            tabIndex={hasSubKPIs ? 0 : undefined}
            onKeyDown={
                hasSubKPIs
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setIsExpanded(!isExpanded);
                          }
                      }
                    : undefined
            }
            aria-expanded={hasSubKPIs ? isExpanded : undefined}
            aria-label={
                hasSubKPIs
                    ? `${isExpanded ? "Collapse" : "Expand"} ${formatStatName(parentKPI.statName)} sub-metrics`
                    : undefined
            }
        >
            {isChanged && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-general-blue rounded-full animate-pulse shadow-lg shadow-general-blue/50"></div>
            )}

            {!parentIsActive && (
                <div className="absolute inset-0 bg-surface-container/20 rounded-lg pointer-events-none"></div>
            )}

            <div className={`p-3 sm:p-4 ${hasSubKPIs && "cursor-pointer"}`}>
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <h3
                                className={`text-base font-semibold ${
                                    !parentIsActive ? "text-on-surface-variant" : "text-on-surface"
                                }`}
                            >
                                {formatStatName(parentKPI.statName)}
                            </h3>
                            {hasSubKPIs && (
                                <>
                                    <span
                                        className={`text-xs px-2 py-1 rounded-full ${
                                            !parentIsActive
                                                ? "bg-surface-container text-on-surface-variant"
                                                : "bg-surface-container text-on-surface-variant"
                                        }`}
                                    >
                                        {categoryGroup.weights?.length || 0} sub-metrics
                                    </span>
                                    <div className="ml-auto">
                                        {isExpanded ? (
                                            <ChevronDown className="w-4 h-4 text-on-surface-variant" />
                                        ) : (
                                            <ChevronRight className="w-4 h-4 text-on-surface-variant" />
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <p
                            className={`text-sm mb-4 leading-relaxed ${!parentIsActive ? "text-on-surface-variant" : "text-on-surface-variant"}`}
                        >
                            {parentKPI.description}
                        </p>

                        <div className="flex items-end gap-2 sm:gap-3 interactive-zone">
                            <div className="flex flex-col items-center">
                                <span
                                    className={`text-xs ${!parentIsActive ? "text-on-surface-variant" : "text-on-surface-variant"} block mb-[8px]`}
                                >
                                    Active
                                </span>
                                <label className="relative inline-flex items-center cursor-pointer mb-1">
                                    <input
                                        type="checkbox"
                                        checked={parentIsActive}
                                        onChange={(e) => onInputChange(parentKPI.id, "isActive", e.target.checked)}
                                        disabled={isLoading}
                                        className="sr-only peer"
                                        aria-label={`Toggle ${formatStatName(parentKPI.statName)} active state`}
                                    />
                                    <div
                                        className={`w-9 h-5 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-general-blue/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-disabled:opacity-50 ${
                                            parentIsActive ? "bg-general-blue" : "bg-surface-container"
                                        }`}
                                    ></div>
                                </label>
                            </div>

                            <div className="flex-1">
                                <label className="flex items-center justify-between text-xs text-on-surface-variant mb-1">
                                    <span>Weight</span>
                                    {parentKPI.defaultWeight !== null && (
                                        <span className="text-xs text-on-surface-variant">
                                            Def: {parentKPI.defaultWeight}
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={getCurrentValue(parentKPI, "weight") ?? ""}
                                    onChange={(e) => onInputChange(parentKPI.id, "weight", e.target.value)}
                                    placeholder={parentKPI.weight === null ? "N/A" : "Weight"}
                                    disabled={isWeightDisabled}
                                    className="w-full px-2 py-1 text-xs bg-surface-container border border-outline-variant/20 rounded text-on-surface placeholder-on-surface-variant focus:outline-none focus:border-general-blue/50 focus:ring-1 focus:ring-general-blue/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed number-input-no-arrows"
                                    aria-label="Weight value for parent KPI"
                                />
                            </div>

                            <div className="flex-1">
                                <label className="flex items-center justify-between text-xs text-on-surface-variant mb-1">
                                    <span>Max Points</span>
                                    {parentKPI.defaultMaxPoints !== null && (
                                        <span className="text-xs text-on-surface-variant">
                                            Def: {parentKPI.defaultMaxPoints}
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="number"
                                    step="1"
                                    value={getCurrentValue(parentKPI, "maxPoints") ?? ""}
                                    onChange={(e) => onInputChange(parentKPI.id, "maxPoints", e.target.value)}
                                    placeholder={parentKPI.maxPoints === null ? "N/A" : "Max pts"}
                                    disabled={isMaxPointsDisabled}
                                    className="w-full px-2 py-1 text-xs bg-surface-container border border-outline-variant/20 rounded text-on-surface placeholder-on-surface-variant focus:outline-none focus:border-general-blue/50 focus:ring-1 focus:ring-general-blue/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed number-input-no-arrows"
                                    aria-label="Maximum points value for parent KPI"
                                />
                            </div>

                            <div className="flex items-end">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => onResetToDefault(parentKPI)}
                                            className="cursor-pointer p-1 text-on-surface hover:text-on-surface-variant hover:bg-surface-container rounded transition-colors"
                                            disabled={isLoading}
                                        >
                                            <RotateCcw className="w-3 h-3" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Reset to default values</TooltipContent>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {hasSubKPIs && isExpanded && (
                <div
                    className="border-t border-outline-variant/30 bg-surface-container-low p-3 sm:p-4 pt-3"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="mb-3">
                        <span
                            className={`text-sm font-medium ${!parentIsActive ? "text-on-surface-variant" : "text-on-surface-variant"}`}
                        >
                            Contributing Metrics
                        </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {categoryGroup.weights?.map((subKPI: Multiplier) => (
                            <MultiplierCard
                                key={subKPI.id}
                                multiplier={subKPI}
                                editedMultipliers={editedMultipliers}
                                onInputChange={onInputChange}
                                onResetToDefault={onResetToDefault}
                                isLoading={isLoading}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
